import React, { useState, useEffect, useCallback } from "react";
import { Box, Divider, HStack, Link, Text, VStack, Spinner, Button, Input } from "@chakra-ui/react";
import { FaAngleRight, FaArrowLeft } from "react-icons/fa";
import { RightSidebarHeader } from "components/Layout/RightSidebarWidget";
import { BookOpenText, MagnifyingGlass } from "@phosphor-icons/react";
import { useQuery } from "react-query";
import { apiGetKBPage, apiListKBPages, apiGetKBPageMetadata, apiSearchKBPages, getKBPageTitle } from "api/kb.service";
import MarkdownText from "components/MarkdownText";
import { useLocation } from "react-router-dom";
import A from "components/A";
import DevOnly from "context/Auth/DevOnly";
import LocalSpinner from "components/LocalSpinner";

const SUPPORT_URL = "mailto:support@esg360.io?subject=Support%20Request";

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

export default function KnowledgeBase() {
  const [pageIdStack, setPageIdStack] = useState([]);
  const pageId = pageIdStack.length > 0 ? pageIdStack[pageIdStack.length - 1] : null;
  const [title, setTitle] = useState("Knowledge Base");
  // HACK the specific path for if you want to make a page for this path
  const [specificPath, setSpecificPath] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebounce(searchQuery, 500);
  const isSearching = debouncedQuery.trim().length > 0;
  const location = useLocation();


  // Reset search and page stack when the user navigates to a different page
  useEffect(() => {
    setSearchQuery("");
    setPageIdStack([]);
    setTitle("Knowledge Base");
  }, [location.pathname]);

  // functions to navigate the pageId stack
  const go = useCallback((pageId) => {
    setPageIdStack((prev) => [...prev, pageId]);
  }, []);
  const back = useCallback(() => {
    setPageIdStack((prev) => prev.slice(0, -1));
  }, []);
  const canBack = pageIdStack.length > 1 || (pageIdStack.length === 1 && isSearching);

  console.log(pageIdStack);

  return (
    <Box p={4}>
      <RightSidebarHeader Icon={BookOpenText}>{title}</RightSidebarHeader>

      <HStack bg="gray.50" borderRadius="md" px={3} py={2} mb={4}>
        <MagnifyingGlass size={16} color="gray" />
        <Input
          placeholder="Search knowledge base..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            // Clear page navigation when user starts searching
            if (e.target.value && pageId) {
              setPageIdStack([]);
              setTitle("Knowledge Base");
            }
          }}
          variant="unstyled"
          size="sm"
        />
      </HStack>


      {pageId && <PageContent pageId={pageId} setPageId={go} setTitle={setTitle} back={back} canBack={canBack} isSearching={isSearching} />}

      {!pageId && isSearching && <SearchResults query={debouncedQuery} go={go} />}

      {!pageId && !isSearching && <PageList setPageId={go} setTitle={setTitle} setSpecificPath={setSpecificPath} />}

      <Divider />
      <Box textAlign="center" mt={8} p={4}>
        {!pageId && !isSearching && <GreenCircle />}

        <Text flex={1}>
          {" "}
          Have a question?{" "}
          <Link color="green.400" fontWeight="bold" href={SUPPORT_URL}>
            Get in touch
          </Link>
        </Text>
      </Box>
      {specificPath && !isSearching && (
        <DevOnly>
          <Box>
            <Text>To make a KB page for here specifically, use this path in the page title: {specificPath}</Text>
          </Box>
        </DevOnly>
      )}
    </Box>
  );
}

function PageContent({ pageId, setPageId, setTitle, back, canBack, isSearching }) {
  // get a page?
  const { data: pageText, isLoading: isLoadingPage } = useQuery(["kb-page", pageId], () => apiGetKBPage(pageId), {
    enabled: !!pageId,
  });

  const { data: metadata } = useQuery(["kb-page-metadata", pageId], () => apiGetKBPageMetadata(pageId), {
    enabled: !!pageId,
  });

  useEffect(() => {
    if (metadata) {
      setTitle(getKBPageTitle(metadata));
    } else {
      setTitle("");
    }
  }, [metadata, setTitle]);

  // poke the backend to refresh its cache from Notion
  const handleRefresh = async () => {
    apiGetKBPage(pageId, true);
    // TODO invalidateQuery
    apiGetKBPageMetadata(pageId, true).then((data) => {
      setTitle(getKBPageTitle(data));
    });
  };

  const KBLink = ({ href, children, ...props }) => {
    // is it a Notion page id?
    let onClick = null;
    const notionLinkRegex = /(https:\/\/www\.notion\.so)?\/([a-z0-9\\-]+)$/;
    if (href?.match(notionLinkRegex)) {
      const linkId = href.match(notionLinkRegex)[2];
      onClick = (e) => {
        console.log("KBLink nav", linkId);
        setPageId(linkId);
        e.preventDefault();
        e.stopPropagation();
      };
      href = ""; // TODO support links to KB pages for tabs and sharing?
    }
    // remove /app if it exists from the text shown
    if (typeof children === "string") {
      children = children.replace(/^\/app\S*/, "").trim();
    }
    return (
      <A href={href} onClick={onClick} {...props} style={{ cursor: "pointer", textDecoration: "underline" }}>
        {children}
      </A>
    );
  };

  return (
    <Box>
      <HStack spacing={3} mb={4} cursor={canBack ? "pointer" : "default"} onClick={back}>
        <FaArrowLeft
        // boxSize={5}
        />
        <Text fontSize="sm" color="green.500">
          {isSearching ? "Search results" : "Guide"}
        </Text>
      </HStack>
      {/* NOTE: LocalSpinner shows up at few places and disappears at some. */}
      {isLoadingPage && <LocalSpinner />}
      {!isLoadingPage && (
        <>
          <MarkdownText allowHtml components={{ a: KBLink }}>
            {pageText}
          </MarkdownText>
          <DevOnly>
            {metadata?.url && (
              <Box mt={4} textAlign="right">
                <Button onClick={handleRefresh}>Refresh</Button>
                <a href={metadata?.url} target="_blank" rel="noopener noreferrer">
                  Edit
                </a>
              </Box>
            )}
          </DevOnly>
        </>
      )}
    </Box>
  );
}

function getPath(location) {
  let path = location.pathname;
  // HACK - special lookup for tab-index
  let searchParams = new URLSearchParams(location.search);
  if (searchParams.has("rtab")) {
    path += "/rtab=" + searchParams.get("rtab");
  }
  if (searchParams.has("tab")) {
    path += "/tab=" + searchParams.get("tab");
  }
  return path;
}

function PageList({ setPageId, setTitle, setSpecificPath }) {
  const location = useLocation();
  // get a list of pages?
  const fullPath = getPath(location);
  useEffect(() => {
    setSpecificPath(fullPath);
  }, [fullPath, setSpecificPath]);

  const { data: listPages, isLoading: isLoadingListPages } = useQuery(["kb-page-list", fullPath], async () => {
    // loop up the path until we find a page
    let path = fullPath;
    while (true) {
      let pages = await apiListKBPages(path);
      // only one result? go to it
      if (pages.length === 1) {
        console.log("one page", pages[0]);
        if (fullPath === path) {
          setSpecificPath(null); // HACK we have a specific path for this page :)
        }
        setPageId(pages[0].id);
        setTitle(getKBPageTitle(pages[0]));
        return pages;
      }
      if (pages.length > 1) {
        return pages;
      }
      // no pages? go to the parent
      let path2 = path.split("/").slice(0, -1).join("/");
      console.log("no pages? go to the parent", path, path2);
      if (path2 === "/" || path2 === "") {
        path2 = "knowledge"; // This is a safer root than an empty string
      }
      if (path2 === path) {
        console.log("no pages :(", path);
        return [];
      }
      path = path2;
    }
  }); // ./useQuery listPages

  console.log("listPages", listPages);
  return (
    <>
      <Divider mb={4} />

      <VStack align="stretch" spacing={2} mb={8}>
        {isLoadingListPages && (
          <VStack py={8} spacing={3}>
            <Spinner />
            <Text fontSize="sm" color="gray.500">Loading content...</Text>
          </VStack>
        )}
        {listPages?.map((item) => (
          <HStack
            key={item.id}
            justifyContent="space-between"
            p={3}
            borderRadius="md"
            bg="white"
            boxShadow="sm"
            _hover={{ bg: "gray.100" }}
            cursor="pointer"
            w="100%"
            onClick={() => {
              setPageId(item.id);
              setTitle(getKBPageTitle(item));
            }}
          >
            <Text fontSize="body.s">{getKBPageTitle(item)}</Text>
            <FaAngleRight color="green.400" />
          </HStack>
        ))}
      </VStack>
    </>
  );
}

function SearchResults({ query, go }) {
  const { data: results, isLoading } = useQuery(
    ["kb-search", query],
    () => apiSearchKBPages(query),
    { enabled: !!query }
  );

  return (
    <>
      <Divider mb={4} />
      <VStack align="stretch" spacing={2} mb={8}>
        {isLoading && (
          <VStack py={8} spacing={3}>
            <Spinner />
            <Text fontSize="sm" color="gray.500">Searching...</Text>
          </VStack>
        )}
        {!isLoading && results?.length === 0 && (
          <Text color="gray.500" fontSize="sm" textAlign="center" py={4}>
            No results found
          </Text>
        )}
        {results?.map((item) => (
          <HStack
            key={item.id}
            justifyContent="space-between"
            p={3}
            borderRadius="md"
            bg="white"
            boxShadow="sm"
            _hover={{ bg: "gray.100" }}
            cursor="pointer"
            w="100%"
            onClick={() => go(item.id)}
          >
            <Text fontSize="body.s">{getKBPageTitle(item)}</Text>
            <FaAngleRight color="green.400" />
          </HStack>
        ))}
      </VStack>
    </>
  );
}

function GreenCircle() {
  return (
    <Box
      border="2px dotted"
      borderColor="green.400"
      borderRadius="full"
      w="100px"
      h="100px"
      mx="auto"
      mb={4}
      position="relative"
    >
      <Box
        position="absolute"
        top="45%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="green.400"
        w="8px"
        h="8px"
        borderRadius="full"
      />
    </Box>
  );
}
